/**
 * SettingsPage — Issue #47
 *
 * Integration credentials management for the 4 MVP connectors:
 *   Gmail, Google Drive, Asana, MLS
 *
 * Backend contract:
 *   GET  /settings/integrations             → { integrations: Integration[] }
 *   PUT  /settings/integrations/{key}       → { key, status: "connected" }   (apiKey connectors)
 *   DELETE /settings/integrations/{key}     → { key, status: "disconnected" }
 *   GET  /settings/integrations/google/start → { url }                       (OAuth redirect)
 *
 * Degrades gracefully when the backend is unavailable (404 / network error).
 * Secrets are never displayed — inputs are write-only.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAINative } from "../providers/AINativeProvider.js";
import { IntegrationCard } from "./settings/IntegrationCard.js";
import type { ApiClient } from "../providers/AINativeProvider.js";
import type { Integration } from "./settings/types.js";

// ─── Types (re-exported for tests) ───────────────────────────────────────────

export type { Integration };

export interface IntegrationsResponse {
  integrations: Integration[];
}

// ─── Fallback skeleton when the backend is not yet wired ─────────────────────

const OFFLINE_INTEGRATIONS: Integration[] = [
  {
    key: "gmail",
    label: "Gmail",
    status: "disconnected",
    authType: "oauth",
  },
  {
    key: "gdrive",
    label: "Google Drive",
    status: "disconnected",
    authType: "oauth",
  },
  {
    key: "asana",
    label: "Asana",
    status: "disconnected",
    authType: "apiKey",
  },
  {
    key: "mls",
    label: "MLS",
    status: "disconnected",
    authType: "apiKey",
  },
];

// ─── Navigate helper (injectable for tests) ───────────────────────────────────

export type NavigateFn = (url: string) => void;

export const defaultNavigate: NavigateFn = (url: string) => {
  window.location.href = url;
};

// ─── SettingsPage component ───────────────────────────────────────────────────

interface SettingsPageProps {
  /** Injectable navigate function — override in tests to prevent real navigation. */
  navigate?: NavigateFn;
}

export function SettingsPage({ navigate = defaultNavigate }: SettingsPageProps) {
  const { api } = useAINative();

  // Keep the api in a ref so callbacks don't re-create when the mock returns
  // a new object each render (avoids infinite useEffect loops in tests).
  const apiRef = useRef<ApiClient>(api);
  apiRef.current = api;

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backendOffline, setBackendOffline] = useState(false);

  // Per-card action state: key → "saving" | "disconnecting" | "connecting" | null
  const [actionState, setActionState] = useState<Record<string, string | null>>({});
  // Per-card inline error messages
  const [cardErrors, setCardErrors] = useState<Record<string, string | null>>({});

  const abortRef = useRef<AbortController | null>(null);

  // ── Fetch integration status ───────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setBackendOffline(false);

    try {
      const data = await apiRef.current.call<IntegrationsResponse>("/settings/integrations");
      setIntegrations(data.integrations ?? []);
      setLoading(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      // Check if it's a 404 (backend not wired yet) — show offline state
      const is404 = msg.includes("404") || msg.includes("Not Found");
      setBackendOffline(is404 || msg.includes("network") || msg.includes("fetch") || msg.includes("Failed to fetch"));
      setError(msg);
      setIntegrations(OFFLINE_INTEGRATIONS);
      setLoading(false);
    }
  }, []);  // stable — uses apiRef.current to avoid infinite re-renders

  // ── Mount + re-fetch on return from OAuth ────────────────────────────────

  useEffect(() => {
    void fetchStatus();

    // Re-fetch when the tab/window regains focus (user returns from Google OAuth)
    const handleFocus = () => {
      void fetchStatus();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      abortRef.current?.abort();
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchStatus]);

  // Also re-fetch if query params indicate a completed OAuth flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasGoogleReturn =
      params.has("connected") || params.has("google") || params.get("status") === "connected";
    if (hasGoogleReturn) {
      void fetchStatus();
    }
  }, [fetchStatus]);

  // ── Update a single integration's status locally ─────────────────────────

  const updateIntegration = useCallback((key: string, updates: Partial<Integration>) => {
    setIntegrations((prev) =>
      prev.map((i) => (i.key === key ? { ...i, ...updates } : i))
    );
  }, []);

  const setCardAction = useCallback((key: string, state: string | null) => {
    setActionState((prev) => ({ ...prev, [key]: state }));
  }, []);

  const setCardError = useCallback((key: string, msg: string | null) => {
    setCardErrors((prev) => ({ ...prev, [key]: msg }));
  }, []);

  // ── Google OAuth connect ──────────────────────────────────────────────────

  const handleGoogleConnect = useCallback(async () => {
    setCardAction("gmail", "connecting");
    setCardAction("gdrive", "connecting");
    setCardError("gmail", null);
    setCardError("gdrive", null);

    try {
      const { url } = await apiRef.current.call<{ url: string }>("/settings/integrations/google/start");
      navigate(url);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to start Google OAuth";
      setCardError("gmail", msg);
      setCardError("gdrive", msg);
    } finally {
      setCardAction("gmail", null);
      setCardAction("gdrive", null);
    }
  }, [navigate, setCardAction, setCardError]);

  // ── API key save (Asana, MLS) ─────────────────────────────────────────────

  const handleApiKeySave = useCallback(async (key: string, value: string) => {
    setCardAction(key, "saving");
    setCardError(key, null);

    try {
      const result = await apiRef.current.call<{ key: string; status: "connected" }>(
        `/settings/integrations/${key}`,
        { method: "PUT", body: { value } }
      );
      updateIntegration(key, { status: result.status });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save credentials";
      setCardError(key, msg);
    } finally {
      setCardAction(key, null);
    }
  }, [updateIntegration, setCardAction, setCardError]);

  // ── Disconnect ────────────────────────────────────────────────────────────

  const handleDisconnect = useCallback(async (key: string) => {
    setCardAction(key, "disconnecting");
    setCardError(key, null);

    try {
      await apiRef.current.call<{ key: string; status: "disconnected" }>(
        `/settings/integrations/${key}`,
        { method: "DELETE" }
      );
      // exactOptionalPropertyTypes: strip detail by omitting the key rather than setting undefined
      const updates: Partial<Integration> = { status: "disconnected" };
      updateIntegration(key, updates);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to disconnect";
      setCardError(key, msg);
    } finally {
      setCardAction(key, null);
    }
  }, [updateIntegration, setCardAction, setCardError]);

  // ── Partition integrations ────────────────────────────────────────────────

  const googleIntegrations = integrations.filter((i) => i.key === "gmail" || i.key === "gdrive");
  const otherIntegrations = integrations.filter((i) => i.key !== "gmail" && i.key !== "gdrive");

  // Google connection is shared — both are connected/disconnected together
  const googleConnected = googleIntegrations.some((i) => i.status === "connected");

  return (
    <>
      <style>{SETTINGS_STYLES}</style>

      <div className="settings-page">
        {/* Page header */}
        <header className="settings-page__header">
          <div className="settings-page__title-group">
            <h1 className="settings-page__title">Settings</h1>
            <span className="settings-page__subtitle">Integrations &amp; credentials</span>
          </div>

          {backendOffline && (
            <span className="settings-page__offline-badge" role="note">
              Backend not wired — showing UI preview
            </span>
          )}
        </header>

        {/* Error banner (non-404 errors) */}
        {error && !backendOffline && (
          <div className="settings-error-banner" role="alert">
            <span className="settings-error-banner__icon" aria-hidden="true">⚠</span>
            <span>{error}</span>
            <button
              className="settings-error-banner__retry"
              onClick={() => void fetchStatus()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div
            className="settings-loading"
            aria-busy="true"
            aria-label="Loading integrations"
          >
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="settings-card-skeleton">
                <div className="skel skel--icon" />
                <div className="skel-body">
                  <div className="skel skel--title" />
                  <div className="skel skel--line" />
                </div>
                <div className="skel skel--pill" />
              </div>
            ))}
          </div>
        )}

        {/* Integration cards */}
        {!loading && (
          <div className="settings-grid">
            {/* ── Google section (Gmail + Drive share one OAuth) ── */}
            <section
              className="settings-section"
              aria-label="Google integrations"
            >
              <div className="settings-section__header">
                <h2 className="settings-section__title">Google</h2>
                <p className="settings-section__desc">
                  Gmail and Google Drive share a single Google connection.
                </p>
              </div>

              <div className="settings-section__cards">
                {googleIntegrations.map((integration) => (
                  <IntegrationCard
                    key={integration.key}
                    integration={integration}
                    actionState={actionState[integration.key] ?? null}
                    cardError={cardErrors[integration.key] ?? null}
                    googleConnected={googleConnected}
                    onGoogleConnect={handleGoogleConnect}
                    onApiKeySave={handleApiKeySave}
                    onDisconnect={handleDisconnect}
                  />
                ))}
              </div>

              {/* Shared note when neither is connected */}
              {!googleConnected && (
                <p className="settings-section__note">
                  One &ldquo;Connect Google&rdquo; authorises both Gmail and Drive.
                </p>
              )}
            </section>

            {/* ── API-key connectors ── */}
            <section
              className="settings-section"
              aria-label="Other integrations"
            >
              <div className="settings-section__header">
                <h2 className="settings-section__title">Connections</h2>
                <p className="settings-section__desc">
                  Paste your personal access token or API key to activate each connector.
                </p>
              </div>

              <div className="settings-section__cards">
                {otherIntegrations.map((integration) => (
                  <IntegrationCard
                    key={integration.key}
                    integration={integration}
                    actionState={actionState[integration.key] ?? null}
                    cardError={cardErrors[integration.key] ?? null}
                    googleConnected={false}
                    onGoogleConnect={handleGoogleConnect}
                    onApiKeySave={handleApiKeySave}
                    onDisconnect={handleDisconnect}
                  />
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Scoped styles ─────────────────────────────────────────────────────────────

const SETTINGS_STYLES = `
/* ── Settings page layout ─────────────────────────────────── */
.settings-page {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: 900px;
}

/* ── Header ──────────────────────────────────────────────── */
.settings-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}

.settings-page__title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.settings-page__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(24px, 3vw, 34px);
  letter-spacing: -0.03em;
  color: var(--c-text-primary);
  line-height: 1;
}

.settings-page__subtitle {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-text-muted);
  letter-spacing: 0.04em;
}

.settings-page__offline-badge {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-yellow);
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  white-space: nowrap;
  margin-top: 6px;
}

/* ── Error banner ─────────────────────────────────────────── */
.settings-error-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(239, 68, 68, 0.07);
  border: 1px solid rgba(239, 68, 68, 0.18);
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  font-size: 13px;
  color: #fca5a5;
  margin-bottom: 24px;
}

.settings-error-banner__icon {
  font-size: 14px;
  flex-shrink: 0;
}

.settings-error-banner__retry {
  margin-left: auto;
  color: var(--c-accent);
  font-size: 12px;
  font-family: var(--font-body);
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  text-decoration: underline;
}

.settings-error-banner__retry:hover {
  color: var(--c-accent-hover);
}

/* ── Loading skeletons ────────────────────────────────────── */
.settings-loading {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-card-skeleton {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--c-surface-1);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  padding: 18px 20px;
}

.skel {
  background: var(--c-surface-2);
  border-radius: var(--radius-sm);
  animation: skel-pulse 1.4s ease-in-out infinite;
}

.skel--icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
}

.skel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skel--title {
  height: 14px;
  width: 120px;
}

.skel--line {
  height: 10px;
  width: 200px;
}

.skel--pill {
  width: 80px;
  height: 24px;
  border-radius: 99px;
}

@keyframes skel-pulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.6; }
}

/* ── Grid / sections ──────────────────────────────────────── */
.settings-grid {
  display: flex;
  flex-direction: column;
  gap: 36px;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-section__header {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--c-border);
}

.settings-section__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--c-text-secondary);
}

.settings-section__desc {
  font-size: 12.5px;
  color: var(--c-text-muted);
  line-height: 1.5;
}

.settings-section__cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.settings-section__note {
  font-size: 11.5px;
  color: var(--c-text-muted);
  font-style: italic;
  padding-left: 2px;
}

/* ── Integration card ─────────────────────────────────────── */
.settings-card {
  background: var(--c-surface-1);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: border-color var(--t-base);
}

.settings-card:hover {
  border-color: var(--c-border-strong);
}

.settings-card--connected {
  border-left: 3px solid var(--c-green);
}

.settings-card__top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.settings-card__icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  background: var(--c-surface-2);
  border: 1px solid var(--c-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  color: var(--c-text-secondary);
  overflow: hidden;
}

.settings-card__info {
  flex: 1;
  min-width: 0;
}

.settings-card__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--c-text-primary);
  line-height: 1.3;
}

.settings-card__detail {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-text-muted);
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Status pill */
.settings-status-pill {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 99px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
  white-space: nowrap;
}

.settings-status-pill--connected {
  background: rgba(34, 197, 94, 0.12);
  color: var(--c-green);
  border: 1px solid rgba(34, 197, 94, 0.22);
}

.settings-status-pill--disconnected {
  background: var(--c-surface-2);
  color: var(--c-text-muted);
  border: 1px solid var(--c-border);
}

.settings-status-pill__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}

.settings-status-pill--connected .settings-status-pill__dot {
  background: var(--c-green);
  box-shadow: 0 0 4px rgba(34, 197, 94, 0.6);
}

.settings-status-pill--disconnected .settings-status-pill__dot {
  background: var(--c-text-muted);
}

/* Card actions area */
.settings-card__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Primary button */
.settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-weight: 500;
  font-family: var(--font-body);
  cursor: pointer;
  transition: background var(--t-fast), color var(--t-fast), border-color var(--t-fast), opacity var(--t-fast);
  white-space: nowrap;
  outline-offset: 3px;
  border: none;
}

.settings-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.settings-btn--primary {
  background: var(--c-accent);
  color: var(--c-text-inverse);
}

.settings-btn--primary:hover:not(:disabled) {
  background: var(--c-accent-hover);
}

.settings-btn--secondary {
  background: var(--c-surface-2);
  color: var(--c-text-secondary);
  border: 1px solid var(--c-border-strong);
}

.settings-btn--secondary:hover:not(:disabled) {
  background: var(--c-surface-3);
  color: var(--c-text-primary);
}

.settings-btn--danger {
  background: transparent;
  color: var(--c-red);
  border: 1px solid rgba(239, 68, 68, 0.25);
}

.settings-btn--danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.07);
  border-color: rgba(239, 68, 68, 0.4);
}

/* Row of action buttons */
.settings-card__btn-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* API key input row */
.settings-apikey-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.settings-apikey-input {
  flex: 1;
  min-width: 0;
  background: var(--c-surface-2);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  padding: 8px 12px;
  font-size: 13px;
  font-family: var(--font-mono);
  color: var(--c-text-primary);
  outline-offset: 2px;
  transition: border-color var(--t-fast);
}

.settings-apikey-input:focus {
  outline: none;
  border-color: var(--c-accent);
}

.settings-apikey-input::placeholder {
  color: var(--c-text-muted);
  font-size: 12px;
}

/* Card-level error */
.settings-card__error {
  font-size: 11.5px;
  color: #fca5a5;
  font-family: var(--font-mono);
  padding: 6px 10px;
  background: rgba(239, 68, 68, 0.06);
  border: 1px solid rgba(239, 68, 68, 0.15);
  border-radius: var(--radius-sm);
}

/* Spinner icon */
.settings-spinner {
  display: inline-block;
  animation: settings-spin 0.7s linear infinite;
  opacity: 0.7;
}

@keyframes settings-spin {
  to { transform: rotate(360deg); }
}

/* Entry animation */
.settings-card {
  animation: card-appear 0.25s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.settings-card:nth-child(1) { animation-delay: 0ms; }
.settings-card:nth-child(2) { animation-delay: 50ms; }
.settings-card:nth-child(3) { animation-delay: 100ms; }
.settings-card:nth-child(4) { animation-delay: 150ms; }

@keyframes card-appear {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .settings-apikey-row {
    flex-direction: column;
    align-items: stretch;
  }

  .settings-card__btn-row {
    flex-direction: column;
  }
}
`;
